import React from 'react';
import { FormState } from '../types';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { FileText, MapPin, User, Building2, Calendar, ClipboardCheck, Save, Moon, Sun } from 'lucide-react';
interface HeaderProps {
  state: FormState;
  canSubmit: boolean;
  onSubmit: () => void;
  onSaveDraft: () => void;
  lastSavedAt: Date | null;
  outstandingCount: number;
  onOpenChecklist: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}
export function Header({
  state,
  canSubmit,
  onSubmit,
  onSaveDraft,
  lastSavedAt,
  outstandingCount,
  onOpenChecklist,
  theme,
  onToggleTheme
}: HeaderProps) {
  return <Card className="mb-6 bg-white dark:bg-slate-800 border-none shadow-md overflow-hidden" data-id="element-34">
      <div className="bg-indigo-900 dark:bg-indigo-950 px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-35">
        <div data-id="element-36">
          <div className="flex items-center space-x-3 mb-1" data-id="element-37">
            <h1 className="text-2xl font-bold text-white" data-id="element-38">
              {state.borrowerName}
            </h1>
            <Badge variant="info" className="bg-indigo-800 text-indigo-100 border-indigo-700" data-id="element-39">
              {state.loanNumber}
            </Badge>
            <Badge variant="default" className="bg-white/10 text-white border-white/20" data-id="element-40">
              {state.disclosureStatus}
            </Badge>
          </div>
          <p className="text-indigo-200 text-sm flex items-center" data-id="element-41">
            <MapPin className="w-4 h-4 mr-1" data-id="element-42" />
            {state.propertyStreet}, {state.propertyState}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1" data-id="element-43">
          <div className="flex items-center space-x-3" data-id="element-44">
            <button onClick={onToggleTheme} className="relative inline-flex items-center justify-center rounded-md border border-white/20 bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40" aria-label="Toggle dark mode" data-id="element-45">
              {theme === 'dark' ? <Sun className="h-4 w-4" data-id="element-46" /> : <Moon className="h-4 w-4" data-id="element-47" />}
            </button>
            <button onClick={onOpenChecklist} className="relative inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40" data-id="element-48">
              <ClipboardCheck className="h-4 w-4" data-id="element-49" />
              Readiness
              {outstandingCount > 0 && <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white" data-id="element-50">
                  {outstandingCount}
                </span>}
            </button>
            <button onClick={onSaveDraft} className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40" data-id="element-51">
              <Save className="h-4 w-4" data-id="element-52" />
              Save Draft
            </button>
            <Button variant="primary" size="lg" disabled={!canSubmit} onClick={onSubmit} className="bg-white dark:bg-slate-800 text-indigo-900 dark:text-indigo-100 hover:bg-slate-100 dark:hover:bg-slate-700 shadow-sm" data-id="element-53">
              Submit Disclosure Request
            </Button>
          </div>
          {lastSavedAt && <span className="text-xs text-indigo-200" data-id="element-54">
              Draft saved at{' '}
              {lastSavedAt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
            </span>}
        </div>
      </div>
      <CardContent className="p-0" data-id="element-55">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100 dark:divide-slate-700" data-id="element-56">
          <div className="p-4 flex items-start space-x-3" data-id="element-57">
            <FileText className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5" data-id="element-58" />
            <div data-id="element-59">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-60">
                Loan Purpose
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-61">
                {state.loanPurpose}
              </p>
            </div>
          </div>
          <div className="p-4 flex items-start space-x-3" data-id="element-62">
            <User className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5" data-id="element-63" />
            <div data-id="element-64">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-65">
                Loan Officer
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-66">
                {state.loanOfficer}
              </p>
            </div>
          </div>
          <div className="p-4 flex items-start space-x-3" data-id="element-67">
            <Building2 className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5" data-id="element-68" />
            <div data-id="element-69">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-70">
                Lender
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-71">
                {state.lender}
              </p>
            </div>
          </div>
          <div className="p-4 flex items-start space-x-3" data-id="element-72">
            <Calendar className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5" data-id="element-73" />
            <div data-id="element-74">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-75">
                Est. Closing
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-76">
                {state.madisonECD || 'Not Set'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}